import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Pause, Play, RefreshCw, X } from 'lucide-react';
import InfoTip from './InfoTip';
import { formatBytes, useNas } from './NasProvider';

const stripExtension = name => String(name || '').replace(/\.[^.]+$/, '');
const formatTime = ms => (ms ? new Date(ms).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '');

function ReviewGroup({ title, info, count, children }) {
    if (!count) return null;
    return (
        <details className="nas-review-group">
            <summary>
                <span>{title}</span>
                <span className="nas-review-count">{count}</span>
                {info && <InfoTip>{info}</InfoTip>}
            </summary>
            <ul>{children}</ul>
        </details>
    );
}

function LooseRow({ entry, onDecide, busy }) {
    const [work, setWork] = useState(stripExtension(entry.name));
    return (
        <li className="nas-review-row">
            <div className="nas-review-main">
                <strong title={entry.path}>{entry.name}</strong>
                <span>{entry.genre}{entry.author ? ` › ${entry.author}` : ''} · {formatBytes(entry.bytes)}</span>
                <input value={work} onChange={event => setWork(event.target.value)} placeholder="Tên tác phẩm" maxLength={160} aria-label="Tên tác phẩm" />
            </div>
            <div className="nas-review-actions">
                <button type="button" className="nas-btn is-primary is-small" disabled={busy || !work.trim()} onClick={() => onDecide(entry, 'approve', { work })}>Thêm vào kho</button>
                <button type="button" className="nas-btn is-ghost is-small" disabled={busy} onClick={() => onDecide(entry, 'dismiss')}>Bỏ qua</button>
            </div>
        </li>
    );
}

// Admin tool: read-only scan of the existing Audiobook/{Thể loại}/{Tác giả}/{Tác phẩm} tree.
export default function NasScanPanel({ onClose }) {
    const { callApi, watchScan } = useNas();
    const [scan, setScan] = useState(undefined);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState('');
    const stopRef = useRef(false);

    useEffect(() => watchScan(setScan), [watchScan]);
    useEffect(() => () => { stopRef.current = true; }, []);

    const run = async fresh => {
        stopRef.current = false;
        setRunning(true);
        setError('');
        try {
            if (fresh) await callApi('scan-start');
            let failures = 0;
            while (!stopRef.current) {
                try {
                    const data = await callApi('scan-continue');
                    failures = 0;
                    if (data.status !== 'running') break;
                } catch (err) {
                    failures += 1;
                    if (failures >= 3) throw err;
                    await new Promise(resolve => setTimeout(resolve, 3000));
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setRunning(false);
        }
    };

    const decide = async (entry, decision, extra = {}) => {
        setBusyId(entry.id);
        try {
            if (decision === 'hide') {
                await callApi('quarantine', { archiveId: entry.archiveId });
                await callApi('review', { reviewId: entry.id, decision: 'dismiss' });
            } else {
                await callApi('review', { reviewId: entry.id, decision, ...extra });
            }
        } catch (err) {
            window.alert(err.message);
        } finally {
            setBusyId('');
        }
    };

    const report = scan?.report;
    const unfinished = scan?.status === 'running';
    const percent = scan?.total ? Math.round((scan.processed / scan.total) * 100) : 0;

    return (
        <div className="nas-diagnose nas-scan">
            <div className="nas-diagnose-head">
                <strong>
                    Quét dữ liệu NAS cũ
                    <InfoTip>
                        Đọc các thư mục có sẵn trong <b>Audiobook / Thể loại / Tác giả / Tác phẩm</b> để task cũ và mới tự có nút <b>Tải NAS</b>.
                        Chỉ đọc, không di chuyển, đổi tên hay xóa file. Thư mục có thư mục con vẫn tính là 1 tác phẩm (1 link).
                        Sách trùng Kho bản quyền sẽ tự bị ẩn. Có thể quét lại bất cứ lúc nào.
                    </InfoTip>
                </strong>
                <button type="button" className="nas-icon-btn" onClick={onClose} aria-label="Đóng"><X size={16} /></button>
            </div>

            {scan === undefined ? (
                <p className="nas-muted"><Loader2 size={14} className="animate-spin" /> Đang tải…</p>
            ) : (
                <>
                    {(running || unfinished) && (
                        <div className="nas-scan-progress">
                            <div className="nas-progress"><span style={{ width: `${percent}%` }} /></div>
                            <small>Đã quét {scan?.processed || 0}/{scan?.total || 0} thư mục tác giả · {scan?.works || 0} tác phẩm</small>
                        </div>
                    )}
                    {!running && scan?.status === 'done' && report && (
                        <p className="nas-scan-summary">
                            <Check size={14} /> Quét xong lúc {formatTime(scan.finishedAt)}: <b>{report.totalWorks}</b> tác phẩm trong kho, <b>{report.linkedTasks}</b> task đã có link NAS
                            {scan.quarantinedWorks > 0 && <>, {scan.quarantinedWorks} thư mục bị ẩn do trùng Kho bản quyền</>}.
                        </p>
                    )}
                    {error && <p className="nas-error">{error}</p>}
                    <div className="nas-modal-actions is-start">
                        {running ? (
                            <button type="button" className="nas-btn is-ghost is-small" onClick={() => { stopRef.current = true; }}><Pause size={14} /> Tạm dừng</button>
                        ) : unfinished ? (
                            <button type="button" className="nas-btn is-primary is-small" onClick={() => run(false)}><Play size={14} /> Tiếp tục quét</button>
                        ) : (
                            <button type="button" className="nas-btn is-primary is-small" onClick={() => run(true)}><RefreshCw size={14} /> {scan ? 'Quét lại' : 'Bắt đầu quét'}</button>
                        )}
                    </div>

                    {!running && (
                        <div className="nas-review">
                            <ReviewGroup title="Task cần xác nhận" count={report?.uncertain?.length} info="Tên task gần giống một thư mục trên NAS nhưng chưa chắc chắn (tên ngắn, khác tác giả hoặc khớp nhiều thư mục). Bấm Đúng để gắn link NAS cho task.">
                                {report?.uncertain?.map(entry => (
                                    <li key={entry.id} className="nas-review-row">
                                        <div className="nas-review-main">
                                            <strong>{entry.bookTitle}{entry.taskAuthor && <em> · {entry.taskAuthor}</em>}</strong>
                                            <span>→ {entry.work} · {entry.author} · {entry.genre}</span>
                                        </div>
                                        <div className="nas-review-actions">
                                            <button type="button" className="nas-btn is-success is-small" disabled={busyId === entry.id} onClick={() => decide(entry, 'link')}>Đúng</button>
                                            <button type="button" className="nas-btn is-ghost is-small" disabled={busyId === entry.id} onClick={() => decide(entry, 'reject')}>Không phải</button>
                                        </div>
                                    </li>
                                ))}
                            </ReviewGroup>
                            <ReviewGroup title="File audio nằm ngoài thư mục tác phẩm" count={scan?.loose?.length} info="File nằm thẳng trong thư mục Thể loại hoặc Tác giả. Thêm vào kho để tạo link tải riêng cho file đó, hoặc tự tạo thư mục tác phẩm trên NAS, chuyển file vào rồi quét lại.">
                                {scan?.loose?.map(entry => <LooseRow key={entry.id} entry={entry} onDecide={decide} busy={busyId === entry.id} />)}
                            </ReviewGroup>
                            <ReviewGroup title="Thư mục có thể bị trùng" count={report?.duplicates?.length} info="Hai thư mục cùng tác giả có tên gần giống nhau. Nếu đúng là cùng một sách, hãy gộp thủ công trên NAS rồi quét lại. Hệ thống không tự gộp hay xóa.">
                                {report?.duplicates?.map(entry => (
                                    <li key={entry.id} className="nas-review-row">
                                        <div className="nas-review-main">
                                            <strong>{entry.a.work} ↔ {entry.b.work}</strong>
                                            <span>{entry.genre} › {entry.author} · {entry.a.audio} file ({formatBytes(entry.a.bytes)}) / {entry.b.audio} file ({formatBytes(entry.b.bytes)})</span>
                                        </div>
                                        <div className="nas-review-actions">
                                            <button type="button" className="nas-btn is-ghost is-small" disabled={busyId === entry.id} onClick={() => decide(entry, 'dismiss')}>Đã xử lý</button>
                                        </div>
                                    </li>
                                ))}
                            </ReviewGroup>
                            <ReviewGroup title="Thư mục không có file audio" count={scan?.empty?.length} info="Thư mục tác phẩm trống hoặc chỉ có file khác (ảnh, pdf…), nên không đưa vào kho.">
                                {scan?.empty?.map(entry => (
                                    <li key={entry.id} className="nas-review-row">
                                        <div className="nas-review-main">
                                            <strong>{entry.work}</strong>
                                            <span>{entry.genre} › {entry.author} · {entry.files} file khác</span>
                                        </div>
                                        <div className="nas-review-actions">
                                            <button type="button" className="nas-btn is-ghost is-small" disabled={busyId === entry.id} onClick={() => decide(entry, 'dismiss')}>Bỏ qua</button>
                                        </div>
                                    </li>
                                ))}
                            </ReviewGroup>
                            <ReviewGroup title="Không còn trên NAS" count={report?.missing?.length} info="Có trong kho nhưng lần quét này không thấy thư mục (đã bị đổi tên, di chuyển hoặc xóa). Ẩn để nhân viên không bấm vào link hỏng.">
                                {report?.missing?.map(entry => (
                                    <li key={entry.id} className="nas-review-row">
                                        <div className="nas-review-main">
                                            <strong>{entry.work}</strong>
                                            <span>{entry.genre} › {entry.author}</span>
                                        </div>
                                        <div className="nas-review-actions">
                                            <button type="button" className="nas-btn is-ghost is-small" disabled={busyId === entry.id} onClick={() => decide(entry, 'hide')}>Ẩn khỏi kho</button>
                                            <button type="button" className="nas-btn is-ghost is-small" disabled={busyId === entry.id} onClick={() => decide(entry, 'dismiss')}>Bỏ qua</button>
                                        </div>
                                    </li>
                                ))}
                            </ReviewGroup>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
